import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { MobileFrame } from "@/components/MobileFrame";
import { Icon } from "@/components/primitives";
import { useAuth } from "@/hooks/useAuth";
import {
  disposeLocalPhoto,
  makeLocalPhoto,
  uploadPhotos,
  type LocalPhoto,
} from "@/lib/photos";
import { toast } from "@/lib/toast";

// VerifyExisting — interstitial page between a quick-posted item and the
// /analyze flow. The user lands here from "Verify now" on /post or in the
// edit sheet. Reads the post's existing photos + brand + category from
// sessionStorage (stashed by the parent page), shows three optional
// upload slots so the user can attach a brand-tag close-up + construction
// shot if those weren't in the original quick-post, then forwards to
// /analyze with either the combined photo set (Verify now) or just the
// existing photos (Skip and verify now).

const NEW_SLOTS = [
  { key: "brand", label: "Brand tag" },
  { key: "construction", label: "Construction detail" },
  { key: "extra", label: "Anything else" },
] as const;

export default function VerifyExisting() {
  const nav = useNavigate();
  const { user } = useAuth();

  // ─── Existing post meta pulled from sessionStorage on mount ─────────
  const [existingId, setExistingId] = useState<string | null>(null);
  const [existingPhotos, setExistingPhotos] = useState<string[]>([]);
  const [existingMeta, setExistingMeta] = useState<{ title?: string; brand?: string; category?: string }>({});

  useEffect(() => {
    try {
      const id = sessionStorage.getItem("fig_verify_existing_id");
      const photos = sessionStorage.getItem("fig_verify_existing_photos");
      const meta = sessionStorage.getItem("fig_verify_existing_meta");
      if (!id || !photos) {
        // No verify-existing context — user opened this page directly.
        // Bounce them back to the closet.
        toast.error("Nothing to verify here");
        nav("/you", { replace: true });
        return;
      }
      setExistingId(id);
      setExistingPhotos(JSON.parse(photos) as string[]);
      if (meta) setExistingMeta(JSON.parse(meta));
    } catch {
      toast.error("Couldn't load this item");
      nav("/you", { replace: true });
    }
  }, [nav]);

  // ─── Optional additional photo slots ─────────────────────────────────
  const [slotPhotos, setSlotPhotos] = useState<Record<string, LocalPhoto | undefined>>({});
  const inputRefs = useRef<Record<string, HTMLInputElement | null>>({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    return () => {
      Object.values(slotPhotos).forEach((p) => p && disposeLocalPhoto(p));
    };
    // eslint-disable-next-line
  }, []);

  const handleSlotFile = async (slotKey: string, file: File) => {
    try {
      const made = await makeLocalPhoto(file);
      setSlotPhotos((prev) => {
        const old = prev[slotKey];
        if (old) disposeLocalPhoto(old);
        return { ...prev, [slotKey]: made };
      });
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Couldn't load that photo";
      toast.error(msg);
    }
  };

  // ─── Submission — both buttons funnel here, "skip" means no upload step ─
  const submit = async (mode: "with-new" | "skip") => {
    if (!existingId || busy) return;
    setBusy(true);
    try {
      let combinedPhotos = [...existingPhotos];

      if (mode === "with-new") {
        const newLocals = NEW_SLOTS
          .map((s) => slotPhotos[s.key])
          .filter((p): p is LocalPhoto => !!p);

        if (newLocals.length > 0) {
          if (!user) {
            toast.error("Sign in required");
            setBusy(false);
            return;
          }
          const sessionId = `verify-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
          const uploadedUrls = await uploadPhotos(user.id, sessionId, newLocals);
          // Append new URLs after the existing ones so the existing
          // photos still anchor the verification, with new shots as
          // additional context.
          combinedPhotos = [...existingPhotos, ...uploadedUrls];
        }
      }

      // Re-stash the (possibly extended) photos so /analyze's
      // verify-existing branch picks them up. Meta + id stay the same.
      sessionStorage.setItem("fig_verify_existing_id", existingId);
      sessionStorage.setItem("fig_verify_existing_photos", JSON.stringify(combinedPhotos));
      sessionStorage.setItem("fig_verify_existing_meta", JSON.stringify(existingMeta));

      nav("/analyze");
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Couldn't upload those photos";
      toast.error(msg);
      setBusy(false);
    }
  };

  if (!existingId) {
    return (
      <MobileFrame>
        <div className="paper-bg" style={{ minHeight: "100vh", padding: "24px 20px 40px" }}>
          <div style={{
            fontFamily: "'IBM Plex Mono', monospace", fontSize: 11,
            letterSpacing: "0.15em", color: "hsl(var(--ink-2))",
            textAlign: "center", marginTop: 80,
          }}>
            LOADING…
          </div>
        </div>
      </MobileFrame>
    );
  }

  return (
    <MobileFrame>
      <div className="paper-bg" style={{ minHeight: "100vh", padding: "16px 20px 40px" }}>
        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <button
            onClick={() => nav(-1)}
            aria-label="Back"
            style={{ background: "none", border: "none", padding: 6, cursor: "pointer", color: "hsl(var(--ink))" }}
          >
            {Icon.chev(18, "l")}
          </button>
          <span className="caps">Verify this item</span>
          <div style={{ width: 30 }} />
        </div>

        {/* Title + intent */}
        <div style={{ marginTop: 14 }}>
          <div style={{
            fontFamily: "'Fraunces', 'Playfair Display', serif", fontWeight: 900,
            fontSize: 28, lineHeight: 1.1, color: "hsl(var(--ink))", letterSpacing: "-0.02em",
          }}>
            Add detail shots
          </div>
          <div style={{
            marginTop: 8,
            fontFamily: "'Fraunces', 'Playfair Display', serif",
            fontSize: 14, lineHeight: 1.4, color: "hsl(var(--ink-2))",
          }}>
            Upload or take any photos showing the brand tag and a care tag or close-up if they aren't already shown. Fig uses these together with your existing photos to verify the item.
          </div>
        </div>

        {/* Existing meta the verification will inherit. Reassures the user
            that fig isn't starting from scratch — it'll use the brand,
            category, and any details they already typed. */}
        {(existingMeta.brand || existingMeta.category) && (
          <div style={{
            marginTop: 16,
            border: "1px dashed hsl(var(--ink-2))",
            padding: "10px 12px",
            background: "hsl(var(--paper-2, var(--cream)))",
          }}>
            <div className="caps" style={{ color: "hsl(var(--ink-2))", fontSize: 9 }}>
              Fig will use
            </div>
            <div style={{
              marginTop: 4,
              fontFamily: "'IBM Plex Mono', monospace", fontSize: 11,
              color: "hsl(var(--ink))",
              display: "flex", gap: 8, flexWrap: "wrap",
            }}>
              {existingMeta.brand && (
                <span><span style={{ color: "hsl(var(--ink-2))" }}>Brand:</span> {existingMeta.brand}</span>
              )}
              {existingMeta.category && (
                <span><span style={{ color: "hsl(var(--ink-2))" }}>Category:</span> {existingMeta.category}</span>
              )}
            </div>
            <div style={{
              marginTop: 6,
              fontFamily: "'IBM Plex Mono', monospace", fontSize: 9,
              letterSpacing: "0.06em",
              color: "hsl(var(--ink-2))",
            }}>
              + the {existingPhotos.length} photo{existingPhotos.length === 1 ? "" : "s"} you already uploaded
            </div>
          </div>
        )}

        {/* Three optional upload slots */}
        <div style={{ marginTop: 18, display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8 }}>
          {NEW_SLOTS.map((slot) => {
            const photo = slotPhotos[slot.key];
            return (
              <label
                key={slot.key}
                style={{
                  position: "relative",
                  aspectRatio: "1 / 1",
                  border: photo ? "1px solid hsl(var(--ink))" : "1.5px dashed hsl(var(--ink-2))",
                  background: photo ? "hsl(var(--ink))" : "hsl(var(--paper-2, var(--cream)))",
                  cursor: "pointer",
                  display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
                  padding: 8,
                  overflow: "hidden",
                }}
              >
                <input
                  ref={(el) => { inputRefs.current[slot.key] = el; }}
                  type="file"
                  accept="image/*"
                  capture="environment"
                  style={{ position: "absolute", inset: 0, width: "100%", height: "100%", opacity: 0, cursor: "pointer" }}
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) handleSlotFile(slot.key, file);
                    e.target.value = "";
                  }}
                />
                {photo ? (
                  <img
                    src={photo.previewUrl}
                    alt={slot.label}
                    style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }}
                  />
                ) : (
                  <>
                    <span style={{
                      fontFamily: "'IBM Plex Mono', monospace", fontSize: 20,
                      color: "hsl(var(--ink-2))",
                    }}>+</span>
                    <span style={{
                      marginTop: 4,
                      fontFamily: "'IBM Plex Mono', monospace", fontSize: 8,
                      letterSpacing: "0.06em", textTransform: "uppercase",
                      color: "hsl(var(--ink-2))", textAlign: "center", lineHeight: 1.2,
                    }}>
                      {slot.label}
                    </span>
                  </>
                )}
              </label>
            );
          })}
        </div>

        {/* Primary + secondary action buttons */}
        <div style={{ marginTop: 22, display: "flex", flexDirection: "column", gap: 10 }}>
          <button
            type="button"
            onClick={() => submit("with-new")}
            disabled={busy}
            style={{
              width: "100%",
              padding: "14px 16px",
              background: "hsl(var(--ink))", color: "hsl(var(--ink-fg))",
              border: "1.5px solid hsl(var(--ink))",
              fontFamily: "'IBM Plex Mono', monospace", fontSize: 12,
              letterSpacing: "0.1em", textTransform: "uppercase", fontWeight: 700,
              cursor: busy ? "wait" : "pointer", opacity: busy ? 0.6 : 1,
            }}
          >
            {busy ? "Uploading…" : "Verify now"}
          </button>
          <button
            type="button"
            onClick={() => submit("skip")}
            disabled={busy}
            style={{
              width: "100%",
              padding: "12px 16px",
              background: "transparent", color: "hsl(var(--ink))",
              border: "1px dashed hsl(var(--ink-2))",
              fontFamily: "'IBM Plex Mono', monospace", fontSize: 11,
              letterSpacing: "0.1em", textTransform: "uppercase",
              cursor: busy ? "wait" : "pointer", opacity: busy ? 0.6 : 1,
            }}
          >
            Skip and verify now
          </button>
        </div>
      </div>
    </MobileFrame>
  );
}
